import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";

export default function JourneyDetail() {
  const { slug } = useParams();
  const [j, setJ] = useState(null);
  useEffect(() => { api.get(`/journeys/${slug}`).then((r) => setJ(r.data)).catch(() => setJ(false)); }, [slug]);

  if (j === false) return <PublicLayout><div className="pt-40 px-6 max-w-[1500px] mx-auto"><p className="eyebrow">Not found</p></div></PublicLayout>;
  if (!j) return <PublicLayout><div className="pt-40 px-6 max-w-[1500px] mx-auto"><p className="eyebrow">Loading…</p></div></PublicLayout>;

  const seo = j.seo || {};
  return (
    <PublicLayout>
      <Seo title={seo.title} description={seo.description} og_title={seo.og_title} og_description={seo.og_description} />
      <section className="relative h-[80vh] min-h-[600px] w-full overflow-hidden">
        <img src={mediaUrl(j.hero_image)} alt={j.title} className="ken-img absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-jade/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-jade/70" />
        <div className="relative z-10 h-full flex items-end pb-20">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 w-full">
            <p className="eyebrow text-copper">{j.duration_days} days · Privately guided</p>
            <h1 className="mt-5 font-serif text-ivory font-light text-5xl md:text-7xl leading-[1.0] max-w-3xl">{j.title}</h1>
            <p className="mt-6 text-ivory/85 max-w-xl text-[16px] font-light leading-relaxed">{j.summary}</p>
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24 md:py-32">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-8 md:order-1 order-2">
            <p className="eyebrow">Day by day</p>
            <h2 className="mt-5 font-serif text-3xl md:text-5xl text-jade font-light">A considered cadence.</h2>
            <p className="mt-6 text-ink/70 text-[15px] font-light leading-relaxed max-w-xl">{j.description}</p>

            <ol data-testid="itinerary-timeline" className="mt-14 relative">
              <div className="absolute left-[10px] top-2 bottom-2 w-px bg-mist" aria-hidden />
              {j.itinerary?.map((day) => (
                <li key={day.day} data-testid={`itinerary-day-${day.day}`} className="relative pl-12 pb-12 last:pb-0">
                  <span className="absolute left-0 top-1 w-5 h-5 rounded-full border border-jade bg-ivory flex items-center justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-copper" />
                  </span>
                  <p className="eyebrow text-jade">Day {day.day}</p>
                  <h3 className="mt-2 font-serif text-2xl md:text-3xl text-jade font-light">{day.title}</h3>
                  <p className="mt-3 text-ink/75 text-[15px] font-light leading-[1.85] max-w-xl">{day.summary}</p>
                </li>
              ))}
            </ol>
          </div>

          <aside className="md:col-span-4 md:order-2 order-1 md:sticky md:top-32 self-start bg-sand p-8 md:p-10">
            <p className="eyebrow">From</p>
            <p className="mt-2 font-serif text-5xl text-jade font-light">£{j.from_price_gbp?.toLocaleString()}</p>
            <p className="mt-1 text-[12px] text-ink/60">per person, twin share, including private guiding</p>
            <div className="divider-thin my-8" />
            <ul className="space-y-4 text-[14px] text-ink/80">
              <li><span className="eyebrow text-ink/60 block">Duration</span>{j.duration_days} days · Privately guided</li>
              <li><span className="eyebrow text-ink/60 block mt-3">Best Months</span>October to April</li>
              <li><span className="eyebrow text-ink/60 block mt-3">Pace</span>Considered & unhurried</li>
            </ul>
            <Link to="/contact" data-testid="journey-cta" className="btn-primary mt-10 w-full">Begin This Journey</Link>
            <p className="mt-4 text-[12px] text-ink/55 italic">Every itinerary is shaped privately around you.</p>
          </aside>
        </div>
      </section>
    </PublicLayout>
  );
}
